import { CheckCircleOutlined, CopyOutlined, EyeOutlined, PlusOutlined, SafetyCertificateOutlined, SaveOutlined, SendOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DocumentTemplateVersion, LayoutCheck, TemplateLayout } from "@wms/domain";
import { Alert, App, Button, Card, Col, DatePicker, Form, Input, Modal, Row, Space, Switch, Table, Tag, Typography } from "antd";
import type { ButtonProps } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { describeError } from "../api/client";
import { formatDate, formatDateTime } from "../api/format";
import { PURPOSE_LABELS, pagesWord, templatesApi } from "../api/renditions";
import { useAuthStore } from "../auth/store";
import { LayoutEditor } from "../components/LayoutEditor";
import { LoadProblem } from "../components/LoadProblem";
import { PageHeader } from "../components/PageHeader";
import { RefSelect } from "../components/RefSelect";
import { AccountingTag, StatusTag } from "./DocumentTemplatesPage";
import { normalizeLayout } from "./templateLayout";

/** One fact of the card: a small muted name over the value, two per row on a wide screen, one on a phone. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <Col xs={24} sm={12}>
      <Typography.Text type="secondary" style={{ fontSize: 12, display: "block" }}>
        {label}
      </Typography.Text>
      <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>{children}</div>
    </Col>
  );
}

type Decision = { kind: "publish" | "archive" | "review"; version: DocumentTemplateVersion };

/** The version opened first: the open draft, else the published one, else the newest. */
function defaultVersion(versions: DocumentTemplateVersion[]): DocumentTemplateVersion | null {
  return versions.find((v) => v.status === "draft" || v.status === "review") ?? versions.find((v) => v.status === "published") ?? versions[0] ?? null;
}

/**
 * KZ-DOC UI02 / TMP03–TMP05: the card of a template, its versions and the block editor of the chosen version. Only a
 * draft is edited; «Проверить макет» shows the problems in words; a version goes «На проверке» → «Опубликован» with a
 * reason and a start date; a published version is read-only — a change is a new version (AT05). Buttons a role may not
 * press are disabled with the reason next to them.
 */
export function DocumentTemplatePage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const can = useAuthStore((s) => s.can);
  const canWrite = can("document_templates.write");
  const canApprove = can("document_templates.approve");
  const template = useQuery({ queryKey: ["document-templates", "one", id], queryFn: () => templatesApi.get(id) });
  const sources = useQuery({ queryKey: ["document-sources"], queryFn: templatesApi.sources, staleTime: 300_000 });
  const versions = useMemo(() => template.data?.versions ?? [], [template.data]);
  const [versionId, setVersionId] = useState<string | null>(null);
  const version = versions.find((v) => v.id === versionId) ?? defaultVersion(versions);
  const editable = !!version && version.status === "draft" && canWrite;
  const [layout, setLayout] = useState<TemplateLayout | null>(null);
  const [period, setPeriod] = useState<{ valid_from: string | null; valid_to: string | null; note: string | null }>({ valid_from: null, valid_to: null, note: null });
  const [dirty, setDirty] = useState(false);
  const [check, setCheck] = useState<LayoutCheck | null>(null);
  const [previewFile, setPreviewFile] = useState<{ blob: Blob; versionId: string } | null>(null);
  const [preview, setPreview] = useState<{ url: string; versionId: string } | null>(null);
  const [decision, setDecision] = useState<Decision | null>(null);
  const [copying, setCopying] = useState(false);
  const [card, setCard] = useState<{ name: string; basis: string } | null>(null);
  const [decisionForm] = Form.useForm();
  const [copyForm] = Form.useForm();

  // a version is opened as it is stored; switching versions or a fresh answer of the server resets the editor
  useEffect(() => {
    if (!version) return;
    setLayout(normalizeLayout(version.layout));
    setPeriod({ valid_from: version.valid_from, valid_to: version.valid_to, note: version.note });
    setCheck(version.check_result ?? null);
    setDirty(false);
  }, [version?.id, version?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (template.data) setCard({ name: template.data.name, basis: template.data.basis ?? "" });
  }, [template.data]);
  useEffect(() => {
    if (!previewFile) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(previewFile.blob);
    setPreview({ url, versionId: previewFile.versionId });
    return () => URL.revokeObjectURL(url);
  }, [previewFile]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["document-templates"] });
    queryClient.invalidateQueries({ queryKey: ["renditions"] });
  };
  const fail = (e: unknown) => message.error(describeError(e), 8);

  const save = useMutation({
    mutationFn: () => templatesApi.patchVersion(version!.id, { layout: layout!, ...period }),
    onSuccess: () => {
      setDirty(false);
      setCheck(null);
      refresh();
    },
    onError: fail,
  });
  /** «Проверить», «Отправить», «Предпросмотр» work on the stored draft: unsaved edits are saved first. */
  const saveFirst = async () => {
    if (editable && dirty) await save.mutateAsync();
  };
  const validate = useMutation({
    mutationFn: async () => {
      await saveFirst();
      return templatesApi.validate(version!.id);
    },
    onSuccess: (result) => {
      setCheck(result);
      if (result.ok) message.success("Макет прошёл проверку");
      refresh();
    },
    onError: fail,
  });
  const submit = useMutation({
    mutationFn: async () => {
      await saveFirst();
      return templatesApi.submit(version!.id);
    },
    onSuccess: () => {
      message.success("Версия отправлена на утверждение");
      refresh();
    },
    onError: (e) => {
      fail(e);
      refresh(); // the server stored the check result of the refused version
    },
  });
  const decide = useMutation({
    mutationFn: async ({ kind, versionId: vid, reason, valid_from }: { kind: Decision["kind"]; versionId: string; reason: string; valid_from?: string | null }) => {
      if (kind === "publish") return templatesApi.publish(vid, { reason, valid_from: valid_from ?? null });
      if (kind === "archive") return templatesApi.archive(vid, { reason });
      return templatesApi.accountingReview(vid, { reason: reason || null });
    },
    onSuccess: (_, vars) => {
      message.success(vars.kind === "publish" ? "Версия опубликована" : vars.kind === "archive" ? "Версия отправлена в архив" : "Отмечено: сверено бухгалтером");
      setDecision(null);
      decisionForm.resetFields();
      refresh();
    },
    onError: fail,
  });
  const newVersion = useMutation({
    mutationFn: (fromId: string | null) => templatesApi.newVersion(id, fromId),
    onSuccess: (row) => {
      message.success(`Создан черновик версии ${row.version}`);
      setVersionId(row.id);
      refresh();
    },
    onError: fail,
  });
  const showPreview = useMutation({
    mutationFn: async () => {
      await saveFirst();
      return templatesApi.preview(version!.id);
    },
    onSuccess: (file) => {
      setPreviewFile({ blob: file.blob, versionId: version!.id });
    },
    onError: fail,
  });
  const patchCard = useMutation({
    mutationFn: (body: { name?: string; basis?: string | null; is_active?: boolean }) => templatesApi.patch(id, body),
    onSuccess: () => {
      message.success("Карточка шаблона сохранена");
      refresh();
    },
    onError: fail,
  });
  const copy = useMutation({
    mutationFn: (values: { name: string; company_id: string }) => templatesApi.create({ name: values.name, company_id: values.company_id, base_template_id: id }),
    onSuccess: (row) => {
      message.success(`Создан свой макет «${row.name}»: черновик версии 1`);
      setCopying(false);
      copyForm.resetFields();
      refresh();
      navigate(`/document-templates/${row.id}`);
    },
    onError: fail,
  });

  if (template.isError) return <LoadProblem error={template.error} onRetry={() => template.refetch()} />;
  const t = template.data;
  if (!t || !layout) return <Card loading />;
  const source = sources.data?.sources.find((s) => s.kind === t.doc_kind) ?? null;
  const openDraft = versions.find((v) => v.status === "draft" || v.status === "review");
  const busy = save.isPending || validate.isPending || submit.isPending || decide.isPending || newVersion.isPending;
  const approveReason = canApprove ? null : "Публикация, архив и сверка — у сотрудника с правом «Шаблоны документов: утверждение»";
  const writeReason = canWrite ? null : "Изменения — у сотрудника с правом «Шаблоны документов: черновики версий»";
  const publishReason = !version ? "Нет версии" : approveReason ?? (version.status === "draft" ? "Сначала проверка и отправка на утверждение" : version.status !== "review" ? "Опубликовать можно версию «На проверке»" : null);
  const newVersionReason = writeReason ?? (openDraft ? `Уже есть неопубликованная версия ${openDraft.version}: продолжите её или отправьте в архив` : null);

  return (
    <div data-testid="document-template-page">
      <PageHeader
        back
        title={`${t.name}${version ? ` · версия ${version.version}` : ""}`}
        tags={
          <Space size={4} wrap>
            {version && <StatusTag status={version.status} />}
            {version && <AccountingTag value={version.accounting_review} />}
            {t.is_system && <Tag bordered={false}>системный</Tag>}
            {!t.is_active && <Tag bordered={false} color="warning">отключён</Tag>}
          </Space>
        }
        subtitle={`${t.doc_kind_label} · ${PURPOSE_LABELS[t.purpose] ?? t.purpose} · ${t.company_name ?? "общий макет для всех наших организаций"}`}
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card title="Карточка шаблона" size="small" data-testid="template-card">
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>Название</Typography.Text>
                <Input value={card?.name ?? ""} disabled={!canWrite} maxLength={200} onChange={(e) => setCard((c) => ({ name: e.target.value, basis: c?.basis ?? "" }))} data-testid="template-name" />
              </Col>
              <Col xs={24} md={12}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>Основание формы</Typography.Text>
                <Input.TextArea value={card?.basis ?? ""} disabled={!canWrite} maxLength={500} autoSize={{ minRows: 1, maxRows: 4 }} onChange={(e) => setCard((c) => ({ name: c?.name ?? "", basis: e.target.value }))} />
              </Col>
            </Row>
            <Row gutter={[12, 8]} style={{ marginTop: 12 }}>
              <Fact label="Вид документа">{t.doc_kind_label}</Fact>
              <Fact label="Назначение">{PURPOSE_LABELS[t.purpose] ?? t.purpose}</Fact>
              <Fact label="Наша организация">{t.company_name ?? "общий макет"}</Fact>
              <Fact label="Язык">Русский</Fact>
              <Fact label="Используется">
                <Switch size="small" checked={t.is_active} disabled={!canWrite || patchCard.isPending} onChange={(is_active) => patchCard.mutate({ is_active })} data-testid="template-active" />
              </Fact>
              <Fact label="Технические данные">
                <Typography.Text type="secondary" style={{ fontSize: 12, wordBreak: "break-all" }}>
                  код {t.code}
                  {t.legacy_form ? ` · прежняя форма ${t.legacy_form}` : ""}
                </Typography.Text>
              </Fact>
            </Row>
            {canWrite ? (
              <Space wrap style={{ marginTop: 8 }}>
                <Button
                  icon={<SaveOutlined />}
                  disabled={!card || (card.name === t.name && card.basis === (t.basis ?? ""))}
                  loading={patchCard.isPending}
                  onClick={() => card && patchCard.mutate({ name: card.name.trim(), basis: card.basis.trim() || null })}
                  data-testid="template-card-save"
                >
                  Сохранить карточку
                </Button>
                {!t.company_id && (
                  <Button icon={<CopyOutlined />} onClick={() => setCopying(true)} data-testid="template-copy-for-company">
                    Создать свой макет для организации
                  </Button>
                )}
              </Space>
            ) : (
              <Typography.Text type="secondary" style={{ display: "block", marginTop: 8 }}>
                {writeReason}
              </Typography.Text>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card
            title="Версии"
            size="small"
            extra={
              <Button size="small" icon={<PlusOutlined />} disabled={!!newVersionReason} loading={newVersion.isPending} onClick={() => newVersion.mutate(version?.id ?? null)} data-testid="template-new-version">
                Новая версия
              </Button>
            }
          >
            {newVersionReason && canWrite && (
              <Typography.Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 6 }}>
                {newVersionReason}
              </Typography.Text>
            )}
            <Table<DocumentTemplateVersion>
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={versions}
              scroll={{ x: true }}
              rowClassName={(v) => (v.id === version?.id ? "ant-table-row-selected" : "")}
              onRow={(v) => ({ onClick: () => setVersionId(v.id), style: { cursor: "pointer" } })}
              data-testid="template-versions"
              columns={[
                { title: "Версия", dataIndex: "version", width: 70, render: (v: number, r) => <span data-testid={`template-version-${v}`} data-status={r.status}>{v}</span> },
                { title: "Статус", dataIndex: "status", render: (v: string) => <StatusTag status={v} /> },
                { title: "Действует", key: "period", render: (_: unknown, r) => (r.valid_from || r.valid_to ? `${formatDate(r.valid_from) || "…"} — ${formatDate(r.valid_to) || "…"}` : "без ограничения") },
                { title: "Сверка", dataIndex: "accounting_review", render: (v: string) => <AccountingTag value={v} /> },
                { title: "Опубликовал", key: "pub", render: (_: unknown, r) => (r.published_at ? `${r.published_by ?? ""} ${formatDateTime(r.published_at)}` : "—") },
              ]}
            />
          </Card>
        </Col>
      </Row>

      {version && (
        <Card
          style={{ marginTop: 16 }}
          title={`Версия ${version.version}`}
          extra={<StatusTag status={version.status} />}
          data-testid="template-version-card"
          data-version={version.version}
          data-status={version.status}
        >
          {version.status !== "draft" && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={version.status === "review" ? "Версия на проверке: изменения не вносятся" : version.status === "published" ? "Опубликованная версия не меняется" : "Версия в архиве"}
              description={
                version.status === "published"
                  ? `Сформированные по ней PDF сохраняют её. Для изменений создайте новую версию.${version.reason ? ` Причина публикации: ${version.reason}.` : ""}`
                  : version.reason
                    ? `Причина: ${version.reason}`
                    : undefined
              }
              data-testid="template-version-readonly"
            />
          )}
          <Row gutter={12} style={{ marginBottom: 12 }}>
            <Col xs={24} md={8}>
              <Typography.Text type="secondary" style={{ fontSize: 12, display: "block" }}>Действует с</Typography.Text>
              <DatePicker
                format="DD.MM.YYYY"
                style={{ width: "100%" }}
                disabled={!editable}
                value={period.valid_from ? dayjs(period.valid_from) : null}
                onChange={(d) => {
                  setPeriod((p) => ({ ...p, valid_from: d ? d.format("YYYY-MM-DD") : null }));
                  setDirty(true);
                }}
                placeholder="без ограничения"
              />
            </Col>
            <Col xs={24} md={8}>
              <Typography.Text type="secondary" style={{ fontSize: 12, display: "block" }}>Действует по</Typography.Text>
              <DatePicker
                format="DD.MM.YYYY"
                style={{ width: "100%" }}
                disabled={!editable}
                value={period.valid_to ? dayjs(period.valid_to) : null}
                onChange={(d) => {
                  setPeriod((p) => ({ ...p, valid_to: d ? d.format("YYYY-MM-DD") : null }));
                  setDirty(true);
                }}
                placeholder="без ограничения"
              />
            </Col>
            <Col xs={24} md={8}>
              <Typography.Text type="secondary" style={{ fontSize: 12, display: "block" }}>Замечания к версии</Typography.Text>
              <Input
                value={period.note ?? ""}
                disabled={!editable}
                maxLength={500}
                onChange={(e) => {
                  setPeriod((p) => ({ ...p, note: e.target.value || null }));
                  setDirty(true);
                }}
              />
            </Col>
          </Row>
          {!source && sources.isSuccess && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={`Вид документа «${t.doc_kind}» не подключён на сервере: поля для выбора недоступны`} />
          )}
          <LayoutEditor
            value={layout}
            source={source}
            readOnly={!editable}
            onChange={(next) => {
              setLayout(next);
              setDirty(true);
            }}
          />

          {check && (
            <Alert
              style={{ marginTop: 16 }}
              type={check.ok ? "success" : "error"}
              showIcon
              data-testid="template-check-result"
              data-ok={check.ok}
              message={check.ok ? `Макет прошёл проверку${check.pages_on_sample ? ` · на учебных данных ${pagesWord(check.pages_on_sample)}` : ""}` : "Макет не прошёл проверку"}
              description={
                check.problems.length ? (
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {check.problems.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                ) : undefined
              }
            />
          )}

          <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
            <Space wrap>
              {version.status === "draft" && (
                <>
                  <Button type="primary" icon={<CheckCircleOutlined />} disabled={!canWrite || busy} loading={validate.isPending} onClick={() => validate.mutate()} data-testid="template-validate">
                    Проверить макет
                  </Button>
                  <Button icon={<SaveOutlined />} disabled={!editable || !dirty || busy} loading={save.isPending} onClick={() => save.mutate(undefined, { onSuccess: () => message.success("Черновик сохранён") })} data-testid="template-save">
                    Сохранить черновик
                  </Button>
                  <Button icon={<SendOutlined />} disabled={!canWrite || busy} loading={submit.isPending} onClick={() => submit.mutate()} data-testid="template-submit">
                    Отправить на утверждение
                  </Button>
                </>
              )}
              {(version.status === "draft" || version.status === "review") && (
                <Button disabled={!!publishReason || busy} onClick={() => setDecision({ kind: "publish", version })} data-testid="template-publish">
                  Опубликовать
                </Button>
              )}
              {version.status !== "archived" && (
                <Button danger disabled={!!approveReason || busy} onClick={() => setDecision({ kind: "archive", version })} data-testid="template-archive">
                  В архив
                </Button>
              )}
              {version.accounting_review === "pending" && (
                <Button icon={<SafetyCertificateOutlined />} disabled={!!approveReason || busy} onClick={() => setDecision({ kind: "review", version })} data-testid="template-accounting-confirm">
                  Сверено бухгалтером
                </Button>
              )}
              <Button icon={<EyeOutlined />} disabled={showPreview.isPending || (!!source && !source.sample)} loading={showPreview.isPending} onClick={() => showPreview.mutate()} data-testid="template-preview">
                Предпросмотр
              </Button>
            </Space>
            {version.status === "draft" && publishReason && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }} data-testid="template-publish-reason">
                Опубликовать: {publishReason}
              </Typography.Text>
            )}
            {approveReason && version.status !== "archived" && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {approveReason}
              </Typography.Text>
            )}
            {!canWrite && version.status === "draft" && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {writeReason}
              </Typography.Text>
            )}
            {source && !source.sample && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Для этого вида документа нет учебных данных: предпросмотр — из карточки документа («Документы и печать»).
              </Typography.Text>
            )}
            {editable && dirty && (
              <Typography.Text type="warning" style={{ fontSize: 12 }}>
                Есть несохранённые изменения: «Проверить», «Отправить» и «Предпросмотр» сначала сохранят черновик.
              </Typography.Text>
            )}
          </div>
        </Card>
      )}

      {preview && preview.versionId === version?.id && (
        <Card
          style={{ marginTop: 16 }}
          title="Предпросмотр на учебных данных"
          extra={
            <Button size="small" onClick={() => setPreviewFile(null)}>
              Закрыть
            </Button>
          }
          data-testid="template-preview-card"
        >
          <iframe className="wms-docviewer-pdf" src={preview.url} title="Предпросмотр макета" data-testid="template-preview-pdf" />
        </Card>
      )}

      <Modal
        open={!!decision}
        title={decision?.kind === "publish" ? `Опубликовать версию ${decision.version.version}` : decision?.kind === "archive" ? `В архив: версия ${decision.version.version}` : "Сверено бухгалтером"}
        okText={decision?.kind === "publish" ? "Опубликовать" : decision?.kind === "archive" ? "В архив" : "Подтвердить"}
        okButtonProps={{ danger: decision?.kind === "archive", "data-testid": "template-decision-ok" } as ButtonProps}
        cancelText="Отмена"
        confirmLoading={decide.isPending}
        onCancel={() => setDecision(null)}
        destroyOnHidden
        onOk={async () => {
          if (!decision) return;
          const values = await decisionForm.validateFields();
          decide.mutate({
            kind: decision.kind,
            versionId: decision.version.id,
            reason: (values.reason ?? "").trim(),
            valid_from: values.valid_from ? (values.valid_from as dayjs.Dayjs).format("YYYY-MM-DD") : null,
          });
        }}
      >
        {decision?.kind === "publish" && (
          <Typography.Paragraph type="secondary">
            Опубликованная версия не меняется. Документы с датой с указанного дня будут формироваться по ней; ранее выданные PDF сохраняют свою версию.
          </Typography.Paragraph>
        )}
        {decision?.kind === "review" && (
          <Typography.Paragraph type="secondary">
            Подтверждает, что бухгалтер сверил применимость и редакцию учётного макета. Отметка в каталоге снимется; на документах её не было.
          </Typography.Paragraph>
        )}
        <Form form={decisionForm} layout="vertical" preserve={false}>
          <Form.Item
            name="reason"
            label={decision?.kind === "review" ? "Комментарий бухгалтера" : "Причина"}
            rules={decision?.kind === "review" ? [] : [{ required: true, min: 3, message: "Укажите причину (не короче 3 символов)" }]}
          >
            <Input.TextArea maxLength={300} autoSize={{ minRows: 2, maxRows: 4 }} data-testid="template-decision-reason" />
          </Form.Item>
          {decision?.kind === "publish" && (
            <Form.Item name="valid_from" label="Действует с" extra="Пусто — с даты, указанной в версии, или без ограничения">
              <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} data-testid="template-decision-from" />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        open={copying}
        title="Свой макет для организации"
        okText="Создать черновик"
        cancelText="Отмена"
        confirmLoading={copy.isPending}
        onCancel={() => setCopying(false)}
        destroyOnHidden
        okButtonProps={{ "data-testid": "template-copy-ok" } as ButtonProps}
        onOk={async () => {
          const values = await copyForm.validateFields();
          copy.mutate(values);
        }}
      >
        <Typography.Paragraph type="secondary">
          Копия текущего опубликованного макета. После публикации для выбранной организации она заменит общий макет; другие организации продолжат работать по общему.
        </Typography.Paragraph>
        <Form form={copyForm} layout="vertical" preserve={false} initialValues={{ name: `${t.name} (своя)` }}>
          <Form.Item name="name" label="Название" rules={[{ required: true, min: 3, message: "Не короче 3 символов" }]}>
            <Input maxLength={200} data-testid="template-copy-name" />
          </Form.Item>
          <Form.Item name="company_id" label="Наша организация" rules={[{ required: true, message: "Выберите организацию" }]}>
            <RefSelect entity="companies" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
